import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import styled, { keyframes } from 'styled-components'
import { Input, Select } from '../../../components/shared/StyledComponents'
import { api } from '../../../services/api'
import {
    TIPO_PROYECTO_INTERNO,
    TIPO_PROYECTO_VALUES,
    isTipoProyectoInterno,
    START_AVAILABILITY_VALUES,
    START_AVAILABILITY_DEFAULT,
} from '../../../types/enums'
import { UnifiedTeamRecommendations } from './UnifiedTeamRecommendations'
import { RecommendationModal } from './RecommendationModal'
import { RedmineOpportunityPanel } from './RedmineOpportunityPanel'
import { RedmineProjectSelect } from './RedmineProjectSelect'
import { TeamMemberDates, localIsoDate } from './TeamMemberDates'
import { useAuth } from '../../../contexts/AuthContext'
import {
    COMPLETE_STATE,
    INCOMPLETE_STATE,
    missingFieldsText,
    offerMissingFields,
    type OfferCompletenessInput,
} from '../../../utils/offerCompleteness'
import type { TeamAlignmentPick } from '../../../types/redmine'

// Configuration
const MAX_FILE_SIZE_MB = 20
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  display: flex;
  align-items: center;
  gap: 12px;
`

const SaveIndicator = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textMuted || '#888'};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`

const FormGroupHalf = styled(FormGroup)`
  grid-column: span 1;
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 4px;
`

const TooltipIcon = styled.span<{ 'data-tooltip'?: string }>`
  cursor: help;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.85em;
  opacity: 0.6;
  position: relative;
  display: inline-block;

  &:hover {
    opacity: 1;
  }

  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 120%;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.78rem;
    white-space: normal;
    width: max-content;
    max-width: 260px;
    z-index: 100;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    line-height: 1.4;
  }

  &:hover::before {
    content: '';
    position: absolute;
    bottom: 110%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
    z-index: 100;
    pointer-events: none;
  }
`

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  resize: vertical;
  min-height: 60px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg};
  position: sticky;
  bottom: 0;
`

const FooterButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${({ theme, variant }) =>
        variant === 'primary' ? theme.colors.primary :
            variant === 'danger' ? '#f44336' : theme.colors.border};
  background: ${({ theme, variant }) =>
        variant === 'primary' ? theme.colors.primary :
            variant === 'danger' ? '#f44336' : 'white'};
  color: ${({ theme, variant }) =>
        variant === 'primary' || variant === 'danger' ? 'white' : theme.colors.text};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

// Autocomplete styles
const AutocompleteContainer = styled.div`
  position: relative;
`

const AutocompleteInput = styled(Input)`
  width: 100%;
`

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
`

const AutocompleteItem = styled.div<{ isHighlighted?: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  background: ${({ isHighlighted, theme }) => isHighlighted ? theme.colors.secondary : 'white'};

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
`

// Team assignment styles
const TeamSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`

const TeamMember = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const TeamMemberName = styled.span`
  flex: 1;
  font-weight: 500;
`

const TeamMemberLoad = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const LoadInput = styled(Input)`
  width: 70px;
  min-width: 60px;
  text-align: center;
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #f44336;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #d32f2f;
  }
`

const AddTeamRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`

// Multi-select tags styles
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 42px;
  background: white;
`

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const TagRemove = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;
  line-height: 1;
  opacity: 0.8;
  &:hover { opacity: 1; }
`

const TagInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 100px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

// Role requirements styles
const RoleRequirementRow = styled.div`
  display: grid;
    grid-template-columns: 1.4fr 60px 72px 86px 1.1fr 1.4fr auto;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  position: relative;
`


const SmallInput = styled(Input)`
  padding: ${({ theme }) => theme.spacing.sm};
`

const SmallSelect = styled(Select)`
  padding: ${({ theme }) => theme.spacing.sm};
`

const InputWithSuffix = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const InputSuffix = styled.span`
    font-size: 0.8rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textMuted};
    min-width: 12px;
`

const RoleTechContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-height: 34px;
    padding: 4px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    background: white;
`

const RoleTechTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: 0.7rem;
`

const RoleTechTagRemove = styled.button`
    border: none;
    background: transparent;
    color: white;
    cursor: pointer;
    line-height: 1;
    padding: 0;
`

const RoleTechSelect = styled.select`
    border: none;
    background: transparent;
    font-size: 0.75rem;
    min-width: 115px;
    color: ${({ theme }) => theme.colors.textMuted};

    &:focus {
        outline: none;
    }
`

const LoadInputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const ManualOverrideBadge = styled.span`
    display: inline-block;
    width: fit-content;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #b26a00;
    background: #fff3e0;
    border: 1px solid #ffcc80;
    border-radius: 10px;
    padding: 1px 6px;
`

// File upload styles
const FileUploadArea = styled.div`
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const FileName = styled.span`
  flex: 1;
  font-weight: 500;
`

interface RoleRequirement {
    role: string
    count: number
    cargabilidad: number  // Load percentage required (0-100%)
    certificates: string[]
    tecnologias?: string[]
    horas_totales?: number  // Total estimated hours for this role
    cargabilidad_manual?: boolean  // True when manually overridden
    horas_totales_manual?: boolean  // True when total hours were manually overridden
    start_availability?: string  // Inmediato | 1 mes | 3 meses | 6 meses
}

interface User {
    id: string
    name: string
    role?: string
}

interface TeamAssignment {
    userId: string
    userName: string
    load: number
    // F-C3: normalized to camelCase (were load_manual / horasPerfil_manual).
    loadManual?: boolean
    horasPerfilManual?: boolean
    role?: string  // The role this person is assigned to fill
    totalHoras?: number
    horasPerfil?: number
    fechaInicio?: string
    fechaFin?: string
    /** UI-only: the dates were edited in this member's row, so the save must send them. */
    datesTouched?: boolean
    /** Redmine subproject this role bills to (per-profile breakdown of the linked project). */
    redmineProjectId?: number | null
    redmineIdentifier?: string | null
    redmineProjectName?: string | null
    /** UI-only: the subproject was picked in this session, so the save must send it. */
    redmineTouched?: boolean
}

const EMPTY_FORM = {
    // General
    name: '',
    description: '',
    cliente: '',
    gestor: '',
    sector: '',
    tecnologia: '',
    tipo_proyecto: '',
    time_material: false,
    proyecto_interno: false,
    // Oferta
    oferta_code: '',
    url_carpeta: '',
    estado: 'borrador',
    venta: '',
    coste_interno: '',
    probabilidad: '',
    oferta_fecha_inicio: '',
    oferta_fecha_entrega: '',
    oferta_fecha_resolucion: '',
    responsable: '',
    socios: '',
    motivo_perdida: '',
    // Proyecto
    proyecto_codigo: '',
    redmine_codigo_imputacion: '',
    proyecto_estado: '',
    proyecto_fecha_inicio: '',
    proyecto_fecha_fin: '',
    proyecto_infra: '',
}

type OpportunityForm = typeof EMPTY_FORM

/** A team member as the save sends it: an emptied end goes out as null (see toWireMember). */
type WireTeamMember = Omit<TeamAssignment, 'fechaInicio' | 'fechaFin'> & {
    fechaInicio?: string | null
    fechaFin?: string | null
}

/**
 * What a team member looks like on the wire.
 *
 * - The load/hours "manual" flags are UI-only (F-C3).
 * - fechaInicio/fechaFin go out only once they were edited in the member's row
 *   (a missing date means "keep it"), and stop going out once that save is in
 *   (see withSavedDates): echoing stored dates back used to undo what the server
 *   does to them, such as moving the team with the project's start date.
 *   An emptied end goes out as null, which makes the assignment open-ended.
 * - The Redmine subproject is sent only when it was picked in this session. A
 *   stale null for an untouched member would undo the routing the Redmine link
 *   sets server-side, while an explicit null is how "proyecto global" is saved.
 */
const toWireMember = (member: TeamAssignment): WireTeamMember => {
    const {
        loadManual, horasPerfilManual, fechaInicio, fechaFin, datesTouched,
        redmineTouched, redmineProjectId, redmineIdentifier, redmineProjectName,
        ...rest
    } = member
    return {
        ...rest,
        ...(redmineTouched ? { redmineProjectId, redmineIdentifier, redmineProjectName } : {}),
        ...(datesTouched ? { fechaInicio: fechaInicio || null, fechaFin: fechaFin || null } : {}),
    }
}

/**
 * The team after a save: each member's dates become what the server stored (a
 * new member's default start, a start moved past the person's closed period,
 * an end it did not take), and edited dates stop being resent. A row whose
 * dates changed again while the save was in flight keeps the new edit.
 */
export const withSavedDates = (
    team: TeamAssignment[],
    saved: Array<Record<string, any>> | undefined,
    sent: Array<Record<string, any>>,
): TeamAssignment[] => {
    if (!Array.isArray(saved)) return team
    const stored = new Map(saved.map((row) => [String(row.user_id ?? row.userId), row]))
    const sentById = new Map(sent.map((m) => [String(m.userId), m]))
    let changed = false
    const next = team.map((m) => {
        const row = stored.get(m.userId)
        if (!row) return m
        if (m.datesTouched) {
            const wire = sentById.get(m.userId)
            const unchanged = wire && 'fechaInicio' in wire
                && (wire.fechaInicio ?? null) === (m.fechaInicio || null)
                && (wire.fechaFin ?? null) === (m.fechaFin || null)
            if (!unchanged) return m
        }
        const fechaInicio = row.fecha_inicio || undefined
        const fechaFin = row.fecha_fin || undefined
        if (!m.datesTouched && m.fechaInicio === fechaInicio && m.fechaFin === fechaFin) return m
        changed = true
        return { ...m, fechaInicio, fechaFin, datesTouched: false }
    })
    return changed ? next : team
}

/** The fields the import's completeness checks read, from the form. */
const completenessOf = (formData: OpportunityForm, roleRequirements: RoleRequirement[]): OfferCompletenessInput => ({
    oferta_code: formData.oferta_code,
    venta: formData.venta,
    coste_interno: formData.coste_interno,
    time_material: formData.time_material,
    interno: isTipoProyectoInterno(formData.tipo_proyecto),
    roleCount: roleRequirements.length,
})

/** 'incompleta' goes out as 'borrador' once nothing is missing, as the server stores it. */
const savedOfferState = (formData: OpportunityForm, roleRequirements: RoleRequirement[]): string =>
    formData.estado === INCOMPLETE_STATE && offerMissingFields(completenessOf(formData, roleRequirements)).length === 0
        ? COMPLETE_STATE
        : formData.estado

/**
 * The save payload, shared by the auto-save and "Guardar Cambios" so both send
 * the same fields. Empty values go out as null (or '' for the infra): the backend
 * clears the column, which is how a date, an amount or 'Sin asignar' is emptied.
 * The Redmine project is not part of it: it is linked through its own endpoint.
 */
export const makeSavePayload = (
    formData: OpportunityForm,
    tecnologias: string[],
    roleRequirements: RoleRequirement[],
    ofertaTeam: TeamAssignment[],
    proyectoTeam: TeamAssignment[],
) => ({
    general: {
        name: formData.name,
        description: formData.description,
        cliente: formData.cliente,
        gestor: formData.gestor,
        sector: formData.sector,
        tecnologia: formData.tecnologia,
        tecnologias: tecnologias,
        tipo_proyecto: formData.tipo_proyecto || null,
        time_material: formData.time_material,
        // F-H1: use shared helper instead of inline Spanish label compare.
        proyecto_interno: isTipoProyectoInterno(formData.tipo_proyecto),
    },
    oferta: {
        estado: savedOfferState(formData, roleRequirements),
        code: formData.oferta_code || null,
        // F-H2 / B-C5: `amount` is deprecated — send only `venta` + `coste_interno`.
        venta: formData.venta ? parseFloat(formData.venta) : null,
        coste_interno: formData.coste_interno ? parseFloat(formData.coste_interno) : null,
        probabilidad: formData.probabilidad || null,
        fecha_inicio: formData.oferta_fecha_inicio || null,
        fecha_entrega: formData.oferta_fecha_entrega || null,
        fecha_resolucion: formData.oferta_fecha_resolucion || null,
        responsable: formData.responsable,
        equipo: ofertaTeam.map(toWireMember),
        roles_requeridos: roleRequirements,
        url_carpeta: formData.url_carpeta || null,
        socios: formData.socios || null,
        motivo_perdida: formData.estado === 'perdida' ? (formData.motivo_perdida || null) : null,
    },
    proyecto: {
        estado: formData.proyecto_estado || undefined,
        codigo: formData.proyecto_codigo || undefined,
        fecha_inicio: formData.proyecto_fecha_inicio || null,
        fecha_fin: formData.proyecto_fecha_fin || null,
        equipo: proyectoTeam.map(toWireMember),
    },
    // '' is 'Sin asignar'; null used to be skipped, so it could never be cleared.
    proyecto_infra: formData.proyecto_infra,
    tecnologias: tecnologias,
    oferta_roles_requeridos: roleRequirements,
})

// Hours of a role requirement: load% * businessDays(range) * 8h * people.
const calcRoleHours = (load: number, peopleCount: number, start: string, end: string): number | null => {
    if (!start || !end) return null
    const startDate = new Date(start)
    const endDate = new Date(end)
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate <= startDate) return null
    let days = 0
    const current = new Date(startDate)
    while (current <= endDate) {
        const day = current.getDay()
        if (day !== 0 && day !== 6) days++
        current.setDate(current.getDate() + 1)
    }
    if (days <= 0) return null
    const safeCount = Math.max(1, peopleCount)
    return Math.max(0, Math.round((Math.max(0, load) / 100) * days * 8 * safeCount))
}

const recalcRoleRequirement = (req: RoleRequirement, start: string, end: string): RoleRequirement => {
    if (req.horas_totales_manual) return req
    const calculated = calcRoleHours(req.cargabilidad, req.count, start, end)
    if (calculated === null) return req
    return { ...req, horas_totales: calculated }
}

interface OpportunityData {
    id?: string
    general: {
        name: string
        description?: string
        cliente?: string
        gestor?: string
        sector?: string
        tecnologia?: string
        tecnologias?: string[]
        proyecto_interno?: boolean
    }
    oferta: {
        estado: string
        code?: string | null
        amount?: number | null
        venta?: number | null
        coste_interno?: number | null
        probabilidad?: string | null
        fecha_inicio?: string | null
        fecha_entrega?: string | null
        fecha_resolucion?: string | null
        responsable?: string
        equipo?: WireTeamMember[]
        roles_requeridos?: RoleRequirement[]
        url_carpeta?: string | null
        socios?: string | null
    }
    proyecto: {
        estado?: string
        codigo?: string
        redmineCodigoImputacion?: string
        fecha_inicio?: string | null
        fecha_fin?: string | null
        equipo?: WireTeamMember[]
    }
    proyecto_infra?: string | null  // Infrastructure resource/partition assignment
    equipo?: TeamAssignment[]  // Legacy: general team
    team?: any[]  // Backend format with user_id and capacity_pct
    tecnologias?: string[]  // Alternative location for technologies
    oferta_roles_requeridos?: RoleRequirement[]  // Alternative location for roles
    offerFile?: File | null  // File to upload for the opportunity
    rfpFiles?: File[]  // RFP/Pliego files to upload
}

interface OpportunityModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: (data: OpportunityData) => Promise<number | void>  // Returns the opportunity ID for new opportunities
    onDelete?: (id: string) => Promise<void>
    opportunity?: OpportunityData | null
    sectors: string[]
    technologies: Record<string, string[]>
    users: User[]
    availableRoles?: string[]
    gestorSuggestions?: string[]
    responsableSuggestions?: string[]
    existingOpportunities?: Array<{ id: number | string; name: string; oferta_code?: string | null; proyecto_codigo?: string | null }>
}

export const OpportunityModal: React.FC<OpportunityModalProps> = ({
    isOpen,
    onClose,
    onSave,
    onDelete,
    opportunity,
    sectors,
    technologies,
    users,
    availableRoles = [],
    gestorSuggestions = [],
    responsableSuggestions = [],
    existingOpportunities = [],
}) => {
    const isEditing = !!opportunity?.id

    const [formData, setFormData] = useState<OpportunityForm>(EMPTY_FORM)

    const [ofertaTeam, setOfertaTeam] = useState<TeamAssignment[]>([])
    const [proyectoTeam, setProyectoTeam] = useState<TeamAssignment[]>([])
    const [tecnologias, setTecnologias] = useState<string[]>([])
    const [roleRequirements, setRoleRequirements] = useState<RoleRequirement[]>([])
    const [offerFile, setOfferFile] = useState<File | null>(null)
    const [existingFileName, setExistingFileName] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [rfpFiles, setRfpFiles] = useState<File[]>([])
    const [existingRfpFiles, setExistingRfpFiles] = useState<Array<{ id: number, filename: string }>>([])
    const rfpFileInputRef = useRef<HTMLInputElement>(null)
    const [saving, setSaving] = useState(false)
    const [newOfertaMemberSearch, setNewOfertaMemberSearch] = useState('')
    const [newProyectoMemberSearch, setNewProyectoMemberSearch] = useState('')
    const [showOfertaDropdown, setShowOfertaDropdown] = useState(false)
    const [showProyectoDropdown, setShowProyectoDropdown] = useState(false)
    const [highlightedOfertaIndex, setHighlightedOfertaIndex] = useState(0)
    const [highlightedProyectoIndex, setHighlightedProyectoIndex] = useState(0)
    const ofertaAutocompleteRef = useRef<HTMLDivElement>(null)
    const proyectoAutocompleteRef = useRef<HTMLDivElement>(null)
    const [showGestorDropdown, setShowGestorDropdown] = useState(false)
    const [showResponsableDropdown, setShowResponsableDropdown] = useState(false)
    const [folderValidation, setFolderValidation] = useState<{ valid: boolean; warnings: string[]; folder_name?: string; expected_name?: string } | null>(null)
    const [folderValidating, setFolderValidating] = useState(false)
    const [autoSaveId, setAutoSaveId] = useState<number | null>(null)
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // The record the modal edits: the opened opportunity, or the draft it created.
    const recordId = opportunity?.id || (autoSaveId ? String(autoSaveId) : undefined)

    const hasInitialized = useRef(false)
    // JSON of the payload the server is known to hold. The auto-save only fires
    // when the current payload differs: opening a modal used to PUT the loaded
    // state straight back (clearing "Revisar" and churning the team rows).
    const savedSnapshotRef = useRef<string | null>(null)
    // The payload of a freshly auto-created draft, to tell whether it was ever
    // touched (an untouched draft is deleted when the modal is dismissed).
    const createdSnapshotRef = useRef<string | null>(null)
    const saveInFlightRef = useRef(false)
    const isOpenRef = useRef(isOpen)
    isOpenRef.current = isOpen
    // Bumped whenever the modal opens, closes or switches opportunity. The draft
    // POST checks it when it answers: if the user closed "Nueva" and opened
    // another card meanwhile, isOpen alone reads true again and the draft id
    // would be adopted by that card's modal (its auto-save would then PUT the
    // card's form onto the draft row).
    const openSessionRef = useRef(0)
    useEffect(() => {
        openSessionRef.current += 1
    }, [isOpen, opportunity])
    // Dates the load recalculations last ran for. Loading an opportunity is not
    // a date change, so the recalculation effects must not rewrite loads on open.
    const lastProyectoDatesRef = useRef('')
    const lastRoleDatesRef = useRef('')

    // Drop an auto-created draft that never got past its default payload.
    const discardDraft = (id: number) => {
        const request = onDelete ? onDelete(String(id)) : api.opportunities.delete(id)
        Promise.resolve(request).catch((err) => console.error('Error discarding empty draft:', err))
    }

    const [infraOptions, setInfraOptions] = useState<Array<{ value: string; label: string }>>([])
    // Redmine link shown in the "Proyecto" section: name of the linked project + a
    // counter that makes the panel below reload after a pick/clear.
    const [redmineProjectName, setRedmineProjectName] = useState<string | null>(null)
    const [redmineRefresh, setRedmineRefresh] = useState(0)
    // Per-profile subprojects of the linked Redmine project (a role bills to one of them).
    const [redmineSubprojects, setRedmineSubprojects] = useState<Array<{ id: number; identifier: string; name: string }>>([])
    useEffect(() => {
        const ref = formData.redmine_codigo_imputacion
        if (!ref) { setRedmineSubprojects([]); return }
        let cancelled = false
        api.redmine.getSubprojects(ref)
            .then((res) => { if (!cancelled) setRedmineSubprojects(Array.isArray(res?.data) ? res.data : []) })
            .catch(() => { if (!cancelled) setRedmineSubprojects([]) })
        return () => { cancelled = true }
    }, [formData.redmine_codigo_imputacion, redmineRefresh])
    const subprojectLabel = (name: string) => (name.includes(' - ') ? name.split(' - ').slice(-1)[0] : name)
    // 'Alinear con Redmine': the row bills to where the person actually logs (null: the global project).
    const updateProyectoTeamMemberRoute = (userId: string, sp: { id: number; identifier?: string | null; name: string } | null) => {
        setProyectoTeam((prev) =>
            prev.map((t) => (t.userId === userId
                ? { ...t, redmineProjectId: sp?.id ?? null, redmineIdentifier: sp?.identifier ?? null, redmineProjectName: sp?.name ?? null, redmineTouched: true }
                : t))
        )
    }
    const updateProyectoTeamMemberSubproject = (userId: string, projectId: number | null) => {
        const sp = redmineSubprojects.find((p) => p.id === projectId) || null
        setProyectoTeam((prev) =>
            prev.map((t) => (t.userId === userId
                ? { ...t, redmineProjectId: sp?.id ?? null, redmineIdentifier: sp?.identifier ?? null, redmineProjectName: sp?.name ?? null, redmineTouched: true }
                : t))
        )
    }
    let modalAuthRole: string | undefined
    try {
        modalAuthRole = useAuth().user?.role
    } catch {
        modalAuthRole = undefined
    }
    const canEditRedmine = modalAuthRole?.toLowerCase() === 'admin'
    useEffect(() => {
        // Pass the current opp_id so the backend keeps THIS opportunity's
        // existing assignments in the dropdown (otherwise the currently
        // selected GPU/partition would disappear as soon as we open the
        // modal, since the exclusivity filter hides assigned resources).
        const oppId = opportunity?.id ? parseInt(opportunity.id as string) : null
        api.infrastructure.getProjectOptions(oppId)
            .then(res => setInfraOptions(res.data))
            .catch(() => { /* ignore - user may not have access */ })
    }, [opportunity?.id])

    const filteredClientes = useMemo(() => {
        if (!formData.cliente) return []
        const search = formData.cliente.toLowerCase()
        return gestorSuggestions.filter(g => g.toLowerCase().includes(search) && g.toLowerCase() !== search)
    }, [formData.cliente, gestorSuggestions])

    const filteredResponsables = useMemo(() => {
        if (!formData.responsable) return []
        const search = formData.responsable.toLowerCase()
        return responsableSuggestions.filter(r => r.toLowerCase().includes(search) && r.toLowerCase() !== search)
    }, [formData.responsable, responsableSuggestions])

    // Recommendation modal state
    const [showFullRecommendationModal, setShowFullRecommendationModal] = useState(false)
    const [recommendationModalRole, setRecommendationModalRole] = useState<string | undefined>()

    // Change log state
    interface ChangeLogEntry {
        id: number
        field_changed: string
        old_value: string | null
        new_value: string | null
        changed_by: string
        changed_at: string
    }
    const [changeLog, setChangeLog] = useState<ChangeLogEntry[]>([])
    const [changeLogLoading, setChangeLogLoading] = useState(false)

    // Helper to convert numeric probabilidad back to text (for backward compatibility with old data)
    const normalizeProbabilidad = (prob: string | number | null | undefined): string => {
        if (!prob) return ''
        const probStr = prob.toString().toLowerCase()
        // If already text, return as-is
        if (['baja', 'media', 'alta'].includes(probStr)) return probStr
        // Convert numeric values back to text
        const num = parseInt(probStr)
        if (num <= 25) return 'baja'
        if (num <= 50) return 'media'
        if (num >= 75) return 'alta'
        return probStr
    }

    // Calculate business days between two dates (excluding weekends)
    const getBusinessDays = (start: string, end: string): number => {
        const startDate = new Date(start)
        const endDate = new Date(end)
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate <= startDate) return 0
        let count = 0
        const current = new Date(startDate)
        while (current <= endDate) {
            const day = current.getDay()
            if (day !== 0 && day !== 6) count++
            current.setDate(current.getDate() + 1)
        }
        return count
    }

    // Calculate total weeks between two dates
    const getWeeksBetween = (start: string, end: string): number => {
        const startDate = new Date(start)
        const endDate = new Date(end)
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate <= startDate) return 0
        const diffMs = endDate.getTime() - startDate.getTime()
        return Math.max(1, Math.round(diffMs / (7 * 24 * 60 * 60 * 1000)))
    }

    // Get schedule weeks for oferta phase
    const getOfertaWeeks = (): number => {
        if (!formData.oferta_fecha_inicio || !formData.oferta_fecha_entrega) return 0
        return getWeeksBetween(formData.oferta_fecha_inicio, formData.oferta_fecha_entrega)
    }

    // Get schedule weeks for proyecto phase
    const getProyectoWeeks = (): number => {
        if (!formData.proyecto_fecha_inicio || !formData.proyecto_fecha_fin) return 0
        return getWeeksBetween(formData.proyecto_fecha_inicio, formData.proyecto_fecha_fin)
    }

    // Calculate load% from manual hours and schedule weeks: (hours / weeks / 40) * 100.
    // Capped at 100 like the load inputs (and the backend): more hours than the
    // weeks hold used to show and send loads such as 800%.
    const calcLoadFromHours = (hours: number, weeks: number): number => {
        if (weeks <= 0 || hours <= 0) return 0
        return Math.min(100, Math.round((hours / weeks / 40) * 100))
    }

    // Auto-calculate hours from load %: (load% * businessDays * 8) — uses project dates only
    const calcHoursFromLoad = (load: number): number | null => {
        if (!formData.proyecto_fecha_inicio || !formData.proyecto_fecha_fin) return null
        const days = getBusinessDays(formData.proyecto_fecha_inicio, formData.proyecto_fecha_fin)
        if (days <= 0) return null
        return Math.round((Math.max(0, load) / 100) * days * 8)
    }

    // Role-requirement hours: load% * businessDays * 8h * people, over the project dates.
    const recalculateRoleRequirement = (req: RoleRequirement): RoleRequirement =>
        recalcRoleRequirement(req, formData.proyecto_fecha_inicio, formData.proyecto_fecha_fin)

    // The two recalculation effects below are declared before the load effect on
    // purpose: the load effect records the loaded dates last, so the render that
    // shows them is not mistaken for a date change. On open they used to rewrite
    // the stored loads from the hours, which the auto-save then wrote back.

    // Recalculate role loads when relevant dates change (except manual overrides)
    useEffect(() => {
        const key = [
            formData.proyecto_fecha_inicio, formData.proyecto_fecha_fin,
            formData.oferta_fecha_inicio, formData.oferta_fecha_entrega,
        ].join('|')
        if (key === lastRoleDatesRef.current) return
        lastRoleDatesRef.current = key
        setRoleRequirements((prev) => prev.map(recalculateRoleRequirement))
    }, [formData.proyecto_fecha_inicio, formData.proyecto_fecha_fin, formData.oferta_fecha_inicio, formData.oferta_fecha_entrega])

    // Recalculate proyecto team loads when proyecto dates change (load = hours / weeks / 40 * 100)
    useEffect(() => {
        const key = `${formData.proyecto_fecha_inicio}|${formData.proyecto_fecha_fin}`
        if (key === lastProyectoDatesRef.current) return
        lastProyectoDatesRef.current = key
        const weeks = getProyectoWeeks()
        if (weeks > 0) {
            setProyectoTeam((team) =>
                team.map((m) => {
                    if (!m.horasPerfil) return m
                    const load = calcLoadFromHours(m.horasPerfil, weeks)
                    return { ...m, load }
                })
            )
        }
    }, [formData.proyecto_fecha_inicio, formData.proyecto_fecha_fin])

    // Load opportunity data when editing
    useEffect(() => {
        if (opportunity) {
            const loadedForm: OpportunityForm = {
                name: opportunity.general?.name || '',
                description: opportunity.general?.description || '',
                cliente: opportunity.general?.cliente || '',
                gestor: opportunity.general?.gestor || '',
                sector: opportunity.general?.sector || '',
                tecnologia: opportunity.general?.tecnologia || '',
                tipo_proyecto: (opportunity.general as any)?.proyecto_interno
                    ? TIPO_PROYECTO_INTERNO
                    : ((opportunity.general as any)?.tipo_proyecto || ''),
                time_material: (opportunity.general as any)?.time_material || false,
                proyecto_interno: (opportunity.general as any)?.proyecto_interno || false,
                oferta_code: (opportunity.oferta as any)?.code || '',
                url_carpeta: (opportunity.oferta as any)?.url_carpeta || '',
                estado: opportunity.oferta?.estado || 'borrador',
                venta: opportunity.oferta?.venta?.toString() || opportunity.oferta?.amount?.toString() || '',
                coste_interno: opportunity.oferta?.coste_interno?.toString() || '',
                probabilidad: normalizeProbabilidad(opportunity.oferta?.probabilidad),
                oferta_fecha_inicio: (opportunity.oferta as any)?.fecha_inicio || '',
                oferta_fecha_entrega: (opportunity.oferta as any)?.fecha_entrega || '',
                oferta_fecha_resolucion: (opportunity.oferta as any)?.fecha_resolucion || '',
                responsable: opportunity.oferta?.responsable || '',
                socios: (opportunity.oferta as any)?.socios || '',
                motivo_perdida: (opportunity.oferta as any)?.motivo_perdida || '',
                proyecto_codigo: (opportunity.proyecto as any)?.codigo || '',
                redmine_codigo_imputacion: (opportunity.proyecto as any)?.redmineCodigoImputacion || (opportunity.proyecto as any)?.redmine_codigo_imputacion || '',
                proyecto_estado: opportunity.proyecto?.estado || '',
                proyecto_fecha_inicio: opportunity.proyecto?.fecha_inicio || '',
                proyecto_fecha_fin: opportunity.proyecto?.fecha_fin || '',
                proyecto_infra: (opportunity as any).proyecto_infra || '',
            }
            setFormData(loadedForm)
            setRedmineProjectName((opportunity.proyecto as any)?.redmine_project_name || null)
            // Load technologies
            const techs = opportunity.general?.tecnologias || opportunity.tecnologias || []
            setTecnologias(techs)
            // Load role requirements, with their hours computed for the LOADED
            // project dates (formData still holds the previous ones here).
            const roles = opportunity.oferta?.roles_requeridos || opportunity.oferta_roles_requeridos || []
            const loadedRoles: RoleRequirement[] = roles.map((r: any) => recalcRoleRequirement({
                role: r.role || '',
                count: r.count || 1,
                cargabilidad: r.cargabilidad ?? 100,
                certificates: r.certificates || [],
                tecnologias: r.tecnologias || [],
                horas_totales: r.horas_totales || undefined,
                cargabilidad_manual: Boolean(r.cargabilidad_manual),
                horas_totales_manual: Boolean(r.horas_totales_manual),
                start_availability: r.start_availability || START_AVAILABILITY_DEFAULT,
            }, loadedForm.proyecto_fecha_inicio, loadedForm.proyecto_fecha_fin))
            setRoleRequirements(loadedRoles)
            // Load existing files (separated by category)
            if (opportunity.id) {
                api.opportunities.getFiles(parseInt(opportunity.id as string)).then(response => {
                    const files = response.data || []
                    const ofertaFile = files.find((f: any) => (f.file_category || 'oferta') === 'oferta')
                    const rfpFilesData = files.filter((f: any) => f.file_category === 'rfp')
                    setExistingFileName(ofertaFile ? (ofertaFile.filename || ofertaFile.original_filename || 'Archivo adjunto') : null)
                    setExistingRfpFiles(rfpFilesData.map((f: any) => ({ id: f.id, filename: f.filename || f.original_filename || 'Archivo adjunto' })))
                }).catch(err => {
                    console.error('Error loading files:', err)
                    setExistingFileName(null)
                    setExistingRfpFiles([])
                })
            } else {
                setExistingFileName(null)
                setExistingRfpFiles([])
            }
            setAutoSaveId(null)
            setSaveStatus('idle')
            hasInitialized.current = true
            setOfferFile(null)  // Reset any new file selection
            setRfpFiles([])
            // Load teams - backend returns team array with user_id and capacity_pct
            // Convert to frontend format with userId and load
            const mapBackendTeam = (team?: any[]): TeamAssignment[] => {
                if (!team || !Array.isArray(team)) return []
                return team.map((t: any) => ({
                    userId: String(t.userId || t.user_id),
                    userName: t.userName || t.display_name || t.username || '',
                    load: t.load ?? t.capacity_pct ?? 100,
                    // F-C3: accept any of the historical flag spellings; emit camelCase.
                    loadManual: Boolean(t.loadManual || t.load_manual),
                    horasPerfilManual: Boolean(
                        t.horasPerfilManual || t.horasPerfil_manual || t.horas_perfil_manual,
                    ),
                    role: t.role,
                    totalHoras: t.totalHoras || t.total_horas || undefined,
                    horasPerfil: t.horasPerfil || t.horas_perfil || undefined,
                    fechaInicio: t.fechaInicio || t.fecha_inicio || undefined,
                    fechaFin: t.fechaFin || t.fecha_fin || undefined,
                    redmineProjectId: t.redmineProjectId ?? t.redmine_project_id ?? null,
                    redmineIdentifier: t.redmineIdentifier ?? t.redmine_identifier ?? null,
                    redmineProjectName: t.redmineProjectName ?? t.redmine_project_name ?? null,
                }))
            }
            // The backend returns separate oferta.equipo and proyecto.equipo
            // IMPORTANT: Do NOT fall back to opportunity.team which contains BOTH teams combined
            // Each team must be loaded from its specific source to maintain independence
            const ofertaEquipo = mapBackendTeam(opportunity.oferta?.equipo)
            const proyectoEquipo = mapBackendTeam(opportunity.proyecto?.equipo)
            setOfertaTeam(ofertaEquipo)
            setProyectoTeam(proyectoEquipo)
            // What the server holds right now: the auto-save stays quiet until
            // the user changes something.
            savedSnapshotRef.current = JSON.stringify(
                makeSavePayload(loadedForm, techs, loadedRoles, ofertaEquipo, proyectoEquipo),
            )
            createdSnapshotRef.current = null
            lastProyectoDatesRef.current = `${loadedForm.proyecto_fecha_inicio}|${loadedForm.proyecto_fecha_fin}`
            lastRoleDatesRef.current = [
                loadedForm.proyecto_fecha_inicio, loadedForm.proyecto_fecha_fin,
                loadedForm.oferta_fecha_inicio, loadedForm.oferta_fecha_entrega,
            ].join('|')
        } else {
            // Reset form for new opportunity
            setFormData(EMPTY_FORM)
            setOfertaTeam([])
            setProyectoTeam([])
            setTecnologias([])
            setRoleRequirements([])
            setOfferFile(null)
            setExistingFileName(null)
            setRfpFiles([])
            setExistingRfpFiles([])
            setAutoSaveId(null)
            setSaveStatus('idle')
            hasInitialized.current = false
            savedSnapshotRef.current = null
            createdSnapshotRef.current = null
            lastProyectoDatesRef.current = '|'
            lastRoleDatesRef.current = '|||'
        }
    }, [opportunity, isOpen])

    // Auto-create opportunity when opening modal for "new"
    useEffect(() => {
        if (!isOpen || opportunity || hasInitialized.current || autoSaveId) return
        hasInitialized.current = true

        const defaultName = `Nueva oportunidad ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
        const session = openSessionRef.current
        const createInitial = async () => {
            try {
                const result = await onSave({
                    general: { name: defaultName },
                    oferta: { estado: 'borrador' },
                    proyecto: {},
                })
                if (result && typeof result === 'number') {
                    if (!isOpenRef.current || openSessionRef.current !== session) {
                        // Dismissed (or replaced by another opportunity) while the
                        // draft was being created: nothing was ever typed into it,
                        // so don't leave it behind or hand it to another modal.
                        discardDraft(result)
                        return
                    }
                    // The server now holds an empty form with the default name.
                    const created = JSON.stringify(makeSavePayload({ ...EMPTY_FORM, name: defaultName }, [], [], [], []))
                    savedSnapshotRef.current = created
                    createdSnapshotRef.current = created
                    setAutoSaveId(result)
                    setFormData(prev => ({ ...prev, name: prev.name || defaultName }))
                    setSaveStatus('saved')
                }
            } catch (err) {
                console.error('Error auto-creating opportunity:', err)
            }
        }
        createInitial()
    }, [isOpen, opportunity])

    // Build the save payload (reused by auto-save and manual save)
    const buildSavePayload = useCallback(
        () => makeSavePayload(formData, tecnologias, roleRequirements, ofertaTeam, proyectoTeam),
        [formData, tecnologias, roleRequirements, ofertaTeam, proyectoTeam],
    )

    // Debounced auto-save: only when the payload differs from what the server holds.
    useEffect(() => {
        const currentId = autoSaveId || (opportunity?.id ? parseInt(opportunity.id as string) : null)
        if (!isOpen || !currentId || !hasInitialized.current) return

        const payload = buildSavePayload()
        const snapshot = JSON.stringify(payload)
        if (snapshot === savedSnapshotRef.current) return

        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)

        autoSaveTimerRef.current = setTimeout(async () => {
            setSaveStatus('saving')
            saveInFlightRef.current = true
            try {
                const res = await api.opportunities.update(currentId, { ...payload, id: currentId })
                // The rows show the dates the server stored; edited ones stop going
                // out, so the snapshot is the payload those rows make from now on.
                const saved = res?.data
                const savedOferta = withSavedDates(ofertaTeam, saved?.oferta?.equipo, payload.oferta.equipo)
                const savedProyecto = withSavedDates(proyectoTeam, saved?.proyecto?.equipo, payload.proyecto.equipo)
                savedSnapshotRef.current = JSON.stringify(
                    makeSavePayload(formData, tecnologias, roleRequirements, savedOferta, savedProyecto),
                )
                setOfertaTeam((team) => withSavedDates(team, saved?.oferta?.equipo, payload.oferta.equipo))
                setProyectoTeam((team) => withSavedDates(team, saved?.proyecto?.equipo, payload.proyecto.equipo))
                setSaveStatus('saved')
            } catch (err) {
                console.error('Auto-save error:', err)
                setSaveStatus('error')
            } finally {
                saveInFlightRef.current = false
            }
        }, 1500)

        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
        }
    }, [formData, tecnologias, roleRequirements, ofertaTeam, proyectoTeam, autoSaveId, isOpen, opportunity, buildSavePayload])

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
        }
    }, [])

    // What an offer the SharePoint import marked incompleta still lacks. Once
    // nothing does, the form shows 'Borrador', which is what the save stores.
    const missingOfferFields = formData.estado === INCOMPLETE_STATE
        ? offerMissingFields(completenessOf(formData, roleRequirements))
        : []
    useEffect(() => {
        if (formData.estado === INCOMPLETE_STATE && missingOfferFields.length === 0) {
            setFormData((prev) => (prev.estado === INCOMPLETE_STATE ? { ...prev, estado: COMPLETE_STATE } : prev))
        }
    }, [formData.estado, missingOfferFields.length])

    // Validate SharePoint folder naming when url_carpeta changes
    useEffect(() => {
        const oppId = opportunity?.id || autoSaveId
        if (!oppId || !formData.url_carpeta) {
            setFolderValidation(null)
            return
        }
        setFolderValidating(true)
        const timer = setTimeout(() => {
            api.opportunities.validateFolder(typeof oppId === 'string' ? parseInt(oppId) : oppId)
                .then(res => setFolderValidation(res.data))
                .catch(() => setFolderValidation(null))
                .finally(() => setFolderValidating(false))
        }, 500)
        return () => clearTimeout(timer)
    }, [formData.url_carpeta, opportunity?.id, autoSaveId])

    // Load change log for existing opportunities
    useEffect(() => {
        if (isOpen && opportunity?.id) {
            setChangeLogLoading(true)
            api.opportunities.getChangeLog(parseInt(opportunity.id as string))
                .then(response => {
                    setChangeLog(response.data || [])
                })
                .catch(err => {
                    console.error('Error loading change log:', err)
                    setChangeLog([])
                })
                .finally(() => setChangeLogLoading(false))
        } else {
            setChangeLog([])
        }
    }, [opportunity, isOpen])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ofertaAutocompleteRef.current && !ofertaAutocompleteRef.current.contains(e.target as Node)) {
                setShowOfertaDropdown(false)
            }
            if (proyectoAutocompleteRef.current && !proyectoAutocompleteRef.current.contains(e.target as Node)) {
                setShowProyectoDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    if (!isOpen) return null

    const handleChange = (field: string, value: string) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value }
            // Recalculate loads when oferta dates change (load = hours / weeks / 40 * 100)
            if (field === 'oferta_fecha_inicio' || field === 'oferta_fecha_entrega') {
                const start = field === 'oferta_fecha_inicio' ? value : prev.oferta_fecha_inicio
                const end = field === 'oferta_fecha_entrega' ? value : prev.oferta_fecha_entrega
                if (start && end) {
                    const weeks = getWeeksBetween(start, end)
                    if (weeks > 0) {
                        setOfertaTeam((team) =>
                            team.map((m) => {
                                if (!m.horasPerfil) return m
                                const load = calcLoadFromHours(m.horasPerfil, weeks)
                                return { ...m, load }
                            })
                        )
                    }
                }
            }
            return next
        })
    }

    // Date validation helper
    const validateDates = (): string | null => {
        const { oferta_fecha_inicio, oferta_fecha_entrega, oferta_fecha_resolucion, proyecto_fecha_inicio, proyecto_fecha_fin } = formData

        // Validate inicio < entrega < resolucion
        if (oferta_fecha_inicio && oferta_fecha_entrega) {
            if (new Date(oferta_fecha_entrega) < new Date(oferta_fecha_inicio)) {
                return 'La fecha de entrega debe ser posterior a la fecha de inicio'
            }
        }

        if (oferta_fecha_entrega && oferta_fecha_resolucion) {
            if (new Date(oferta_fecha_resolucion) < new Date(oferta_fecha_entrega)) {
                return 'La fecha de resolución debe ser posterior a la fecha de entrega'
            }
        }

        // Validate project dates: end >= start
        if (proyecto_fecha_inicio && proyecto_fecha_fin) {
            if (new Date(proyecto_fecha_fin) < new Date(proyecto_fecha_inicio)) {
                return 'La fecha fin de proyecto debe ser igual o posterior a la fecha inicio de proyecto'
            }
        }

        return null
    }

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            alert('El nombre es obligatorio')
            return
        }

        // Validate roles - no empty roles allowed
        const emptyRoles = roleRequirements.filter(r => !r.role)
        if (emptyRoles.length > 0) {
            alert('Todos los roles requeridos deben tener un rol seleccionado')
            return
        }

        // Validate dates
        const dateError = validateDates()
        if (dateError) {
            alert(dateError)
            return
        }

        // Validate uniqueness of name, oferta code, and proyecto code
        const currentId = recordId
        const others = existingOpportunities.filter(o => String(o.id) !== String(currentId))

        const duplicateName = others.find(o => o.name.toLowerCase() === formData.name.trim().toLowerCase())
        if (duplicateName) {
            alert(`Ya existe una oportunidad con el nombre "${formData.name.trim()}"`)
            return
        }

        if (formData.oferta_code.trim()) {
            const duplicateOfertaCode = others.find(o => o.oferta_code && o.oferta_code.toLowerCase() === formData.oferta_code.trim().toLowerCase())
            if (duplicateOfertaCode) {
                alert(`Ya existe una oportunidad con el código de oferta "${formData.oferta_code.trim()}"`)
                return
            }
        }

        if (formData.proyecto_codigo.trim()) {
            const duplicateProyectoCodigo = others.find(o => o.proyecto_codigo && o.proyecto_codigo.toLowerCase() === formData.proyecto_codigo.trim().toLowerCase())
            if (duplicateProyectoCodigo) {
                alert(`Ya existe una oportunidad con el código de proyecto "${formData.proyecto_codigo.trim()}"`)
                return
            }
        }

        setSaving(true)
        try {
            // Same payload as the auto-save: this path used to build its own, which
            // dropped tipo_proyecto / T&M / motivo_perdida and re-sent the Redmine
            // code (wiping the resolved link on every save).
            await onSave({ ...buildSavePayload(), id: currentId, offerFile, rfpFiles })
            onClose()
        } catch (error: any) {
            console.error('Error saving opportunity:', error)
            const rawDetail = error?.response?.data?.detail
            const detail = typeof rawDetail === 'string' ? rawDetail : null
            const status = error?.response?.status
            if (status === 409) {
                alert(detail || 'Ya existe una oportunidad con ese nombre, código de oferta o código de proyecto')
            } else if (!status || status >= 502) {
                alert('El servicio no está disponible en este momento. Inténtalo de nuevo en unos minutos.')
            } else {
                alert(detail || 'Error al guardar la oportunidad. Consulta con el administrador.')
            }
        } finally {
            setSaving(false)
        }
    }

    // Cancelar / × / click outside. A draft auto-created on open that never got
    // anything beyond its default payload is discarded, instead of piling up as
    // "Nueva oportunidad <fecha>" rows. Unsaved edits (still in the debounce) are
    // discarded as before; a save in flight keeps the draft.
    const handleClose = () => {
        if (
            !opportunity && autoSaveId && !saveInFlightRef.current
            && savedSnapshotRef.current === createdSnapshotRef.current
            && !formData.redmine_codigo_imputacion
        ) {
            discardDraft(autoSaveId)
        }
        onClose()
    }

    const handleDelete = async () => {
        if (!recordId || !onDelete) return
        if (!confirm('¿Estás seguro de que deseas eliminar esta oportunidad?')) return

        setSaving(true)
        try {
            await onDelete(recordId)
            onClose()
        } catch (error) {
            console.error('Error deleting opportunity:', error)
            alert('Error al eliminar la oportunidad')
        } finally {
            setSaving(false)
        }
    }

    // Filter users for autocomplete
    const filteredOfertaUsers = users.filter(
        (u) =>
            u.name.toLowerCase().includes(newOfertaMemberSearch.toLowerCase()) &&
            !ofertaTeam.some((t) => t.userId === u.id)
    )

    const filteredProyectoUsers = users.filter(
        (u) =>
            u.name.toLowerCase().includes(newProyectoMemberSearch.toLowerCase()) &&
            !proyectoTeam.some((t) => t.userId === u.id)
    )

    const addOfertaTeamMember = (user: User) => {
        const calculatedHours = calcHoursFromLoad(100)
        setOfertaTeam((prev) => [
            ...prev,
            {
                userId: user.id,
                userName: user.name,
                load: 100,
                loadManual: false,
                horasPerfil: calculatedHours ?? undefined,
                horasPerfilManual: false,
            },
        ])
        setNewOfertaMemberSearch('')
        setShowOfertaDropdown(false)
    }

    const addProyectoTeamMember = (user: User) => {
        setProyectoTeam((prev) => [
            ...prev,
            { userId: user.id, userName: user.name, load: 100 },
        ])
        setNewProyectoMemberSearch('')
        setShowProyectoDropdown(false)
    }

    const updateOfertaTeamMemberHours = (userId: string, hours: number | undefined) => {
        setOfertaTeam((prev) =>
            prev.map((t) => {
                if (t.userId !== userId) return t
                const weeks = getOfertaWeeks()
                const load = hours ? calcLoadFromHours(hours, weeks) : 0
                return { ...t, horasPerfil: hours, load }
            })
        )
    }

    const updateOfertaTeamMemberField = (userId: string, field: keyof TeamAssignment, value: any) => {
        setOfertaTeam((prev) =>
            prev.map((t) => {
                if (t.userId !== userId) return t
                return { ...t, [field]: value }
            })
        )
    }

    const updateProyectoTeamMemberHours = (userId: string, hours: number | undefined) => {
        setProyectoTeam((prev) =>
            prev.map((t) => {
                if (t.userId !== userId) return t
                const weeks = getProyectoWeeks()
                const load = hours ? calcLoadFromHours(hours, weeks) : 0
                return { ...t, horasPerfil: hours, load }
            })
        )
    }

    const updateProyectoTeamMemberLoad = (userId: string, load: number) => {
        setProyectoTeam((prev) =>
            prev.map((t) => (t.userId === userId ? { ...t, load } : t))
        )
    }

    // A member's own dates, edited in their row: the save sends them (datesTouched).
    const updateTeamMemberDates = (
        setTeam: typeof setOfertaTeam, userId: string, fechaInicio: string | undefined, fechaFin: string | undefined,
    ) => {
        setTeam((prev) => prev.map((t) => (t.userId === userId ? { ...t, fechaInicio, fechaFin, datesTouched: true } : t)))
    }

    // 'Alinear con Redmine': the people picked join the project team from today
    // (their row's start can be moved back by hand) and bill to the subproject
    // they already log to, or the global project.
    const addAlignedMembers = (picks: TeamAlignmentPick[]) => {
        const today = localIsoDate()
        setProyectoTeam((prev) => [
            ...prev,
            ...picks
                .filter((p) => !prev.some((m) => m.userId === p.userId))
                .map((p): TeamAssignment => ({
                    userId: p.userId,
                    userName: p.userName,
                    load: p.load,
                    role: p.role,
                    fechaInicio: today,
                    datesTouched: true,
                    redmineProjectId: p.subproject?.id ?? null,
                    redmineIdentifier: p.subproject?.identifier ?? null,
                    redmineProjectName: p.subproject?.name ?? null,
                    redmineTouched: true,
                })),
        ])
    }

    const removeOfertaTeamMember = (userId: string) => {
        setOfertaTeam((prev) => prev.filter((t) => t.userId !== userId))
    }

    const removeProyectoTeamMember = (userId: string) => {
        setProyectoTeam((prev) => prev.filter((t) => t.userId !== userId))
    }

    const handleOfertaKeyDown = (e: React.KeyboardEvent) => {
        if (!showOfertaDropdown) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedOfertaIndex((prev) => Math.min(prev + 1, filteredOfertaUsers.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedOfertaIndex((prev) => Math.max(prev - 1, 0))
        } else if (e.key === 'Enter' && filteredOfertaUsers[highlightedOfertaIndex]) {
            e.preventDefault()
            addOfertaTeamMember(filteredOfertaUsers[highlightedOfertaIndex])
        } else if (e.key === 'Escape') {
            setShowOfertaDropdown(false)
        }
    }

    const handleProyectoKeyDown = (e: React.KeyboardEvent) => {
        if (!showProyectoDropdown) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedProyectoIndex((prev) => Math.min(prev + 1, filteredProyectoUsers.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedProyectoIndex((prev) => Math.max(prev - 1, 0))
        } else if (e.key === 'Enter' && filteredProyectoUsers[highlightedProyectoIndex]) {
            e.preventDefault()
            addProyectoTeamMember(filteredProyectoUsers[highlightedProyectoIndex])
        } else if (e.key === 'Escape') {
            setShowProyectoDropdown(false)
        }
    }

    // Technology categories (high level)
    const techCategories = Object.keys(technologies)

    return (
        <Overlay onClick={handleClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>
                        {isEditing ? '✏️ Editar Oportunidad' : '➕ Nueva Oportunidad'}
                        {saveStatus === 'saving' && <SaveIndicator>Guardando...</SaveIndicator>}
                        {saveStatus === 'saved' && <SaveIndicator style={{ color: '#4caf50' }}>Guardado</SaveIndicator>}
                        {saveStatus === 'error' && <SaveIndicator style={{ color: '#f44336' }}>Error al guardar</SaveIndicator>}
                    </ModalTitle>
                    <CloseButton onClick={handleClose}>&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    {/* Warnings for incomplete fields */}
                    {(() => {
                        const warnings: string[] = []
                        if (!formData.name.trim()) warnings.push('Nombre')
                        if (!formData.proyecto_interno && !formData.cliente.trim()) warnings.push('Cliente')
                        if (!formData.sector) warnings.push('Sector')
                        if (tecnologias.length === 0) warnings.push('Tecnologías')
                        if (!formData.proyecto_interno && ofertaTeam.length === 0) warnings.push('Equipo Oferta')

                        // Additional warnings when project is won (ganada)
                        if (formData.estado === 'ganada') {
                            if (proyectoTeam.length === 0) warnings.push('Equipo de Proyecto')
                            if (!formData.proyecto_codigo.trim()) warnings.push('Código de Proyecto')
                            if (!formData.proyecto_fecha_inicio) warnings.push('Fecha Inicio Proyecto')
                            if (!formData.time_material && !formData.proyecto_fecha_fin) warnings.push('Fecha Fin Proyecto / Time&Material')
                        }

                        return warnings.length > 0 ? (
                            <div style={{
                                background: '#fff3e0',
                                border: '1px solid #ffcc80',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                color: '#e65100',
                                fontSize: '0.9rem',
                            }}>
                                ⚠️ <strong>Campos pendientes:</strong> {warnings.join(', ')}
                            </div>
                        ) : null
                    })()}
                    {missingOfferFields.length > 0 && (
                        <div
                            role="status"
                            style={{
                                background: '#FBE9E7',
                                border: '1px solid #FFAB91',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                color: '#BF360C',
                                fontSize: '0.9rem',
                            }}
                        >
                            🧩 <strong>Oferta incompleta</strong> (importada de SharePoint). Falta: {missingFieldsText(missingOfferFields)}.
                            {' '}Cuando esté completa pasa sola a Borrador.
                        </div>
                    )}

                    <FormSection>
                        <SectionTitle>📋 Información General</SectionTitle>
                        <FormGrid>
                            <FormGroupFull>
                                <Label>Nombre * <TooltipIcon data-tooltip="Nombre identificativo de la oportunidad comercial">&#9432;</TooltipIcon></Label>
                                <Input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="Nombre de la oportunidad"
                                />
                            </FormGroupFull>
                            <FormGroupFull>
                                <Label>Descripción</Label>
                                <TextArea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Descripción breve..."
                                />
                            </FormGroupFull>
                            <FormGroupHalf>
                                <Label>Cliente</Label>
                                <AutocompleteContainer>
                                    <AutocompleteInput
                                        type="text"
                                        value={formData.cliente}
                                        onChange={(e) => {
                                            handleChange('cliente', e.target.value)
                                            setShowGestorDropdown(true)
                                        }}
                                        onFocus={() => setShowGestorDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowGestorDropdown(false), 150)}
                                        placeholder="Nombre del cliente"
                                        autoComplete="off"
                                    />
                                    {showGestorDropdown && formData.cliente && filteredClientes.length > 0 && (
                                        <AutocompleteDropdown>
                                            {filteredClientes.map((g) => (
                                                <AutocompleteItem
                                                    key={g}
                                                    onMouseDown={() => {
                                                        handleChange('cliente', g)
                                                        setShowGestorDropdown(false)
                                                    }}
                                                >
                                                    {g}
                                                </AutocompleteItem>
                                            ))}
                                        </AutocompleteDropdown>
                                    )}
                                </AutocompleteContainer>
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Sector</Label>
                                <Select
                                    value={formData.sector}
                                    onChange={(e) => handleChange('sector', e.target.value)}
                                >
                                    <option value="">Seleccionar</option>
                                    {sectors.map((sector) => (
                                        <option key={sector} value={sector}>
                                            {sector}
                                        </option>
                                    ))}
                                </Select>
                            </FormGroupHalf>
                            <FormGroupFull>
                                <Label>Tecnologías (múltiples)</Label>
                                <TagsContainer>
                                    {tecnologias.map((tech, idx) => (
                                        <Tag key={idx}>
                                            {tech}
                                            <TagRemove onClick={() => setTecnologias(prev => prev.filter((_, i) => i !== idx))}>×</TagRemove>
                                        </Tag>
                                    ))}
                                    <Select
                                        style={{ flex: 1, minWidth: 150, border: 'none' }}
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value && !tecnologias.includes(e.target.value)) {
                                                setTecnologias(prev => [...prev, e.target.value])
                                            }
                                        }}
                                    >
                                        <option value="">+ Añadir tecnología</option>
                                        {techCategories.filter(t => !tecnologias.includes(t)).map((tech) => (
                                            <option key={tech} value={tech}>{tech}</option>
                                        ))}
                                    </Select>
                                </TagsContainer>
                            </FormGroupFull>
                            <FormGroupHalf>
                                <Label>Tipo de Proyecto</Label>
                                <Select
                                    value={formData.tipo_proyecto}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        // F-H1: isInternal check goes through the shared helper so
                                        // the Spanish label only lives in types/enums.ts.
                                        const isInternal = isTipoProyectoInterno(value)
                                        setFormData(prev => ({
                                            ...prev,
                                            tipo_proyecto: value,
                                            proyecto_interno: isInternal,
                                            cliente: isInternal ? 'Plexus' : prev.cliente,
                                            venta: isInternal ? '' : prev.venta,
                                            coste_interno: isInternal ? '' : prev.coste_interno,
                                        }))
                                    }}
                                >
                                    <option value="">Seleccionar</option>
                                    {TIPO_PROYECTO_VALUES.map((tp) => (
                                        <option key={tp} value={tp}>{tp}</option>
                                    ))}
                                </Select>
                            </FormGroupHalf>
                        </FormGrid>
                    </FormSection>

                    <FormSection>
                        <SectionTitle>💼 Oferta</SectionTitle>
                        <FormGrid>
                            <FormGroupHalf>
                                <Label>Código de Oferta <TooltipIcon data-tooltip="Código identificativo de la oferta/propuesta comercial">&#9432;</TooltipIcon></Label>
                                <Input
                                    type="text"
                                    value={formData.oferta_code}
                                    onChange={(e) => handleChange('oferta_code', e.target.value)}
                                    placeholder="PROP-2026-001"
                                />
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Estado <TooltipIcon data-tooltip="Estado actual del proceso de oferta (borrador → presentada → ganada/perdida)">&#9432;</TooltipIcon></Label>
                                <Select
                                    value={formData.estado}
                                    onChange={(e) => handleChange('estado', e.target.value)}
                                >
                                    {/* Only the SharePoint import sets it; once left it cannot be chosen again. */}
                                    {formData.estado === INCOMPLETE_STATE && <option value={INCOMPLETE_STATE}>Incompleta</option>}
                                    <option value="borrador">Borrador</option>
                                    <option value="en_preparacion">En Preparación</option>
                                    <option value="en_revision">En Revisión</option>
                                    <option value="presentada">Presentada</option>
                                    <option value="en_negociacion">En Negociación</option>
                                    <option value="ganada">Ganada</option>
                                    <option value="perdida">Perdida</option>
                                    <option value="cancelada">Cancelada</option>
                                </Select>
                            </FormGroupHalf>
                            {formData.estado === 'perdida' && (
                                <FormGroupFull>
                                    <Label>Motivo de Pérdida <TooltipIcon data-tooltip="Indica el motivo por el que se ha perdido la oferta">&#9432;</TooltipIcon></Label>
                                    <TextArea
                                        value={formData.motivo_perdida}
                                        onChange={(e) => handleChange('motivo_perdida', e.target.value)}
                                        placeholder="Ej: Precio más alto que la competencia, requisitos técnicos no cumplidos..."
                                        style={{ borderColor: !formData.motivo_perdida ? '#ef6c00' : undefined }}
                                    />
                                </FormGroupFull>
                            )}
                            <FormGroupHalf>
                                <Label>Probabilidad <TooltipIcon data-tooltip="Estimación de probabilidad de ganar la oferta (baja=25%, media=50%, alta=75%)">&#9432;</TooltipIcon></Label>
                                <Select
                                    value={formData.probabilidad}
                                    onChange={(e) => handleChange('probabilidad', e.target.value)}
                                >
                                    <option value="">Sin definir</option>
                                    <option value="baja">Baja</option>
                                    <option value="media">Media</option>
                                    <option value="alta">Alta</option>
                                </Select>
                            </FormGroupHalf>
                            {!formData.proyecto_interno && (
                                <>
                                    <FormGroupHalf>
                                        <Label>Venta (€) <TooltipIcon data-tooltip="Precio de venta al cliente final (en euros)">&#9432;</TooltipIcon></Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={formData.venta}
                                            onChange={(e) => handleChange('venta', e.target.value)}
                                            placeholder="Precio de venta al cliente"
                                        />
                                    </FormGroupHalf>
                                    <FormGroupHalf>
                                        <Label>Coste Interno (€) <TooltipIcon data-tooltip="Coste estimado del equipo interno para ejecutar el proyecto">&#9432;</TooltipIcon></Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={formData.coste_interno}
                                            onChange={(e) => handleChange('coste_interno', e.target.value)}
                                            placeholder="Coste interno del equipo"
                                        />
                                    </FormGroupHalf>
                                </>
                            )}
                            <FormGroupHalf>
                                <Label>Comercial</Label>
                                <AutocompleteContainer>
                                    <AutocompleteInput
                                        type="text"
                                        value={formData.responsable}
                                        onChange={(e) => {
                                            handleChange('responsable', e.target.value)
                                            setShowResponsableDropdown(true)
                                        }}
                                        onFocus={() => setShowResponsableDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowResponsableDropdown(false), 150)}
                                        placeholder="Comercial"
                                        autoComplete="off"
                                    />
                                    {showResponsableDropdown && formData.responsable && filteredResponsables.length > 0 && (
                                        <AutocompleteDropdown>
                                            {filteredResponsables.map((r) => (
                                                <AutocompleteItem
                                                    key={r}
                                                    onMouseDown={() => {
                                                        handleChange('responsable', r)
                                                        setShowResponsableDropdown(false)
                                                    }}
                                                >
                                                    {r}
                                                </AutocompleteItem>
                                            ))}
                                        </AutocompleteDropdown>
                                    )}
                                </AutocompleteContainer>
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Fecha Inicio <TooltipIcon data-tooltip="Fecha de inicio de preparación de la oferta">&#9432;</TooltipIcon></Label>
                                <Input
                                    type="date"
                                    value={formData.oferta_fecha_inicio}
                                    onChange={(e) => handleChange('oferta_fecha_inicio', e.target.value)}
                                />
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Fecha Entrega <TooltipIcon data-tooltip="Fecha límite de entrega de la propuesta al cliente">&#9432;</TooltipIcon></Label>
                                <Input
                                    type="date"
                                    value={formData.oferta_fecha_entrega}
                                    onChange={(e) => handleChange('oferta_fecha_entrega', e.target.value)}
                                />
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Fecha Resolución <TooltipIcon data-tooltip="Fecha esperada de decisión del cliente sobre la oferta">&#9432;</TooltipIcon></Label>
                                <Input
                                    type="date"
                                    value={formData.oferta_fecha_resolucion}
                                    onChange={(e) => handleChange('oferta_fecha_resolucion', e.target.value)}
                                />
                            </FormGroupHalf>

                            {/* Role Requirements Section */}
                            <FormGroupFull>
                                <Label>Roles Requeridos <TooltipIcon data-tooltip="Perfiles profesionales necesarios: rol, número de personas y porcentaje de dedicación">&#9432;</TooltipIcon></Label>
                                {roleRequirements.map((req, idx) => (
                                    <RoleRequirementRow key={idx}>
                                        <SmallSelect
                                            value={req.role}
                                            onChange={(e) => {
                                                const updated = [...roleRequirements]
                                                updated[idx] = { ...req, role: e.target.value }
                                                setRoleRequirements(updated)
                                            }}
                                            style={{ fontSize: '0.85rem', padding: '6px 8px' }}
                                        >
                                            <option value="">Seleccionar rol</option>
                                            {availableRoles.map((role) => (
                                                <option key={role} value={role}>{role}</option>
                                            ))}
                                        </SmallSelect>
                                        <InputWithSuffix>
                                            <SmallInput
                                                type="number"
                                                min="1"
                                                value={req.count}
                                                onChange={(e) => {
                                                    const updated = [...roleRequirements]
                                                    const nextReq = { ...req, count: parseInt(e.target.value) || 1 }
                                                    updated[idx] = recalculateRoleRequirement(nextReq)
                                                    setRoleRequirements(updated)
                                                }}
                                                placeholder="Nº"
                                                style={{ width: '46px' }}
                                            />
                                            <InputSuffix>x</InputSuffix>
                                        </InputWithSuffix>
                                        <LoadInputWrapper>
                                            <InputWithSuffix>
                                                <SmallInput
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={req.cargabilidad}
                                                    onChange={(e) => {
                                                        const updated = [...roleRequirements]
                                                        updated[idx] = {
                                                            ...req,
                                                            cargabilidad: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)),
                                                        }
                                                        updated[idx] = recalculateRoleRequirement(updated[idx])
                                                        setRoleRequirements(updated)
                                                    }}
                                                    placeholder="Carga %"
                                                    title="Porcentaje de dedicación"
                                                    style={{
                                                        width: '60px',
                                                        background: 'white',
                                                        fontWeight: 500,
                                                    }}
                                                />
                                                <InputSuffix>%</InputSuffix>
                                            </InputWithSuffix>
                                            {/* Slider para ajustar cargabilidad por sección */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                                <div style={{ width: '140px' }}>
                                                    <input
                                                        type="range"
                                                        min={0}
                                                        max={100}
                                                        value={req.cargabilidad}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value) || 0
                                                            const updated = [...roleRequirements]
                                                            updated[idx] = {
                                                                ...req,
                                                                cargabilidad: val,
                                                            }
                                                            updated[idx] = recalculateRoleRequirement(updated[idx])
                                                            setRoleRequirements(updated)
                                                        }}
                                                        aria-label={`Carga % rol ${req.role || idx}`}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                <div style={{ minWidth: 40, textAlign: 'right', fontWeight: 600 }}>{req.cargabilidad}%</div>
                                            </div>
                                            {req.horas_totales_manual && <ManualOverrideBadge>manual</ManualOverrideBadge>}
                                        </LoadInputWrapper>
                                        <InputWithSuffix>
                                            <SmallInput
                                                type="number"
                                                min="0"
                                                value={req.horas_totales || ''}
                                                readOnly={!req.horas_totales_manual}
                                                onClick={() => {
                                                    if (req.horas_totales_manual) return
                                                    const ok = window.confirm(
                                                        'Las horas se calculan automáticamente con el porcentaje de dedicación y duración. ¿Quieres desbloquearlas para editarlas manualmente?'
                                                    )
                                                    if (!ok) return
                                                    setRoleRequirements((prev) => {
                                                        const updated = [...prev]
                                                        updated[idx] = { ...updated[idx], horas_totales_manual: true }
                                                        return updated
                                                    })
                                                }}
                                                onChange={(e) => {
                                                    const updated = [...roleRequirements]
                                                    const nextReq = {
                                                        ...req,
                                                        horas_totales: parseInt(e.target.value) || undefined,
                                                        horas_totales_manual: true,
                                                    }
                                                    updated[idx] = nextReq
                                                    setRoleRequirements(updated)
                                                }}
                                                placeholder="Horas"
                                                title={req.horas_totales_manual
                                                    ? 'Horas modificadas manualmente'
                                                    : 'Horas calculadas automáticamente'}
                                                style={{
                                                    width: '72px',
                                                    background: req.horas_totales_manual ? '#fff8e1' : '#f5f5f5',
                                                    borderColor: req.horas_totales_manual ? '#ffb74d' : undefined,
                                                    fontWeight: req.horas_totales_manual ? 700 : 500,
                                                }}
                                            />
                                            <InputSuffix>h</InputSuffix>
                                        </InputWithSuffix>
                                        <SmallSelect
                                            value={req.start_availability || START_AVAILABILITY_DEFAULT}
                                            onChange={(e) => {
                                                const updated = [...roleRequirements]
                                                updated[idx] = { ...req, start_availability: e.target.value }
                                                setRoleRequirements(updated)
                                            }}
                                            title="Disponibilidad inicial del perfil"
                                            aria-label={`Disponibilidad inicial rol ${req.role || idx}`}
                                            style={{ fontSize: '0.8rem', padding: '4px 6px', width: '110px' }}
                                        >
                                            {START_AVAILABILITY_VALUES.map((v) => (
                                                <option key={v} value={v}>{v}</option>
                                            ))}
                                        </SmallSelect>
                                        <RemoveButton onClick={() => setRoleRequirements(prev => prev.filter((_, i) => i !== idx))}>×</RemoveButton>
                                    </RoleRequirementRow>
                                ))}
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        const baseReq: RoleRequirement = {
                                            role: '',
                                            count: 1,
                                            cargabilidad: 100,
                                            certificates: [],
                                            tecnologias: [],
                                            horas_totales: undefined,
                                            horas_totales_manual: false,
                                            start_availability: START_AVAILABILITY_DEFAULT,
                                        }
                                        setRoleRequirements(prev => [...prev, recalculateRoleRequirement(baseReq)])
                                    }}
                                    style={{ marginTop: '8px' }}
                                >
                                    + Añadir Rol
                                </Button>
                            </FormGroupFull>

                            {/* Oferta File Upload Section */}
                            <FormGroupFull>
                                <Label>Documento de Oferta (PDF/Word, máx {MAX_FILE_SIZE_MB}MB)</Label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) {
                                            if (file.size > MAX_FILE_SIZE_BYTES) {
                                                alert(`El archivo es demasiado grande (máximo ${MAX_FILE_SIZE_MB}MB)`)
                                                return
                                            }
                                            setOfferFile(file)
                                            setExistingFileName(null)  // Clear existing file when new one selected
                                        }
                                    }}
                                />
                                {offerFile ? (
                                    <FileInfo>
                                        <FileName>📄 {offerFile.name}</FileName>
                                        <RemoveButton onClick={() => setOfferFile(null)}>×</RemoveButton>
                                    </FileInfo>
                                ) : existingFileName ? (
                                    <FileInfo>
                                        <FileName>📄 {existingFileName}</FileName>
                                        <Button variant="secondary" onClick={() => {
                                            // Reset input value to allow selecting same file again
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = ''
                                            }
                                            fileInputRef.current?.click()
                                        }}>
                                            Cambiar
                                        </Button>
                                    </FileInfo>
                                ) : (
                                    <FileUploadArea onClick={() => {
                                        // Reset input value before opening
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = ''
                                        }
                                        fileInputRef.current?.click()
                                    }}>
                                        📁 Click para subir documento de oferta
                                    </FileUploadArea>
                                )}
                            </FormGroupFull>

                            {/* RFP / Pliego File Upload Section */}
                            <FormGroupFull>
                                <Label>Pliego / RFP (PDF/Word/Excel, máx {MAX_FILE_SIZE_MB}MB)</Label>
                                <input
                                    ref={rfpFileInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const files = e.target.files
                                        if (files && files.length > 0) {
                                            const validFiles: File[] = []
                                            for (let i = 0; i < files.length; i++) {
                                                if (files[i].size > MAX_FILE_SIZE_BYTES) {
                                                    alert(`El archivo "${files[i].name}" es demasiado grande (máximo ${MAX_FILE_SIZE_MB}MB)`)
                                                } else {
                                                    validFiles.push(files[i])
                                                }
                                            }
                                            if (validFiles.length > 0) {
                                                setRfpFiles(prev => [...prev, ...validFiles])
                                            }
                                        }
                                    }}
                                />
                                {(rfpFiles.length > 0 || existingRfpFiles.length > 0) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                                        {existingRfpFiles.map((f) => (
                                            <FileInfo key={`existing-${f.id}`}>
                                                <FileName>📋 {f.filename}</FileName>
                                                <RemoveButton
                                                    title="Eliminar archivo"
                                                    onClick={async () => {
                                                        // Deleted right away, like every other edit here is
                                                        // saved: only dropping it from the list brought it
                                                        // back on reopen.
                                                        if (!confirm(`¿Eliminar el archivo "${f.filename}"?`)) return
                                                        try {
                                                            await api.opportunities.deleteFile(f.id)
                                                            setExistingRfpFiles(prev => prev.filter(ef => ef.id !== f.id))
                                                        } catch (err) {
                                                            console.error('Error deleting file:', err)
                                                            alert('No se pudo eliminar el archivo')
                                                        }
                                                    }}
                                                >×</RemoveButton>
                                            </FileInfo>
                                        ))}
                                        {rfpFiles.map((f, idx) => (
                                            <FileInfo key={`new-${idx}`}>
                                                <FileName>📋 {f.name} <span style={{ color: '#4caf50', fontSize: '0.8em' }}>(nuevo)</span></FileName>
                                                <RemoveButton onClick={() => {
                                                    setRfpFiles(prev => prev.filter((_, i) => i !== idx))
                                                }}>×</RemoveButton>
                                            </FileInfo>
                                        ))}
                                    </div>
                                )}
                                <FileUploadArea onClick={() => {
                                    if (rfpFileInputRef.current) {
                                        rfpFileInputRef.current.value = ''
                                    }
                                    rfpFileInputRef.current?.click()
                                }}>
                                    📋 Click para subir pliego / RFP
                                </FileUploadArea>
                            </FormGroupFull>
                            <FormGroupFull>
                                <Label>Socios del Proyecto <TooltipIcon data-tooltip="Posibles socios o empresas colaboradoras del proyecto (texto libre)">&#9432;</TooltipIcon></Label>
                                <TextArea
                                    value={formData.socios}
                                    onChange={(e) => handleChange('socios', e.target.value)}
                                    placeholder="Ej: Empresa A (infraestructura), Empresa B (consultoría)..."
                                />
                            </FormGroupFull>
                            <FormGroupFull>
                                <Label>URL Carpeta de Oferta <TooltipIcon data-tooltip="Enlace a la carpeta externa donde se almacenan los documentos de la oferta">&#9432;</TooltipIcon></Label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Input
                                        type="url"
                                        value={formData.url_carpeta}
                                        onChange={(e) => handleChange('url_carpeta', e.target.value)}
                                        placeholder="https://sharepoint.com/..."
                                        style={{ flex: 1 }}
                                    />
                                    {folderValidating && <span style={{ fontSize: 12, color: '#999' }}>...</span>}
                                    {!folderValidating && folderValidation && (
                                        folderValidation.valid
                                            ? <span title="Carpeta correcta" style={{ color: '#4caf50', fontSize: 18 }}>&#10003;</span>
                                            : <span
                                                title={folderValidation.warnings.join('\n')}
                                                style={{ color: '#ff9800', fontSize: 18, cursor: 'help' }}
                                              >&#9888;</span>
                                    )}
                                </div>
                                {!folderValidating && folderValidation && !folderValidation.valid && (
                                    <div style={{ marginTop: 4, fontSize: 11, color: '#e65100' }}>
                                        {folderValidation.warnings.map((w, i) => <div key={i}>- {w}</div>)}
                                        {folderValidation.expected_name && (
                                            <div style={{ marginTop: 2, color: '#666' }}>Nombre esperado: {folderValidation.expected_name}</div>
                                        )}
                                    </div>
                                )}
                            </FormGroupFull>
                        </FormGrid>
                    </FormSection>

                    <FormSection>
                        <SectionTitle>🚀 Proyecto</SectionTitle>
                        <FormGrid>
                            <FormGroupHalf>
                                <Label>Código de Proyecto</Label>
                                <Input
                                    type="text"
                                    value={formData.proyecto_codigo}
                                    onChange={(e) => handleChange('proyecto_codigo', e.target.value)}
                                    placeholder="PROJ-2026-001"
                                />
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Proyecto Redmine (Imputación)</Label>
                                <RedmineProjectSelect
                                    variant="field"
                                    opportunityId={opportunity?.id ? parseInt(opportunity.id as string) : (autoSaveId ?? undefined)}
                                    identifier={formData.redmine_codigo_imputacion}
                                    projectName={redmineProjectName}
                                    canEdit={canEditRedmine}
                                    onChange={(identifier, project) => {
                                        handleChange('redmine_codigo_imputacion', identifier)
                                        setRedmineProjectName(project?.name || null)
                                        setRedmineRefresh((n) => n + 1)
                                    }}
                                />
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Estado del Proyecto</Label>
                                <Select
                                    value={formData.proyecto_estado}
                                    onChange={(e) => handleChange('proyecto_estado', e.target.value)}
                                >
                                    <option value="">Sin proyecto</option>
                                    <option value="pendiente">Pendiente</option>
                                    <option value="planificacion">Planificación</option>
                                    <option value="en_curso">En Curso</option>
                                    <option value="pausado">Pausado</option>
                                    <option value="completado">Completado</option>
                                    <option value="mantenimiento">Mantenimiento</option>
                                    <option value="cancelado">Cancelado</option>
                                </Select>
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label>Fecha Inicio Proyecto</Label>
                                <Input
                                    type="date"
                                    value={formData.proyecto_fecha_inicio}
                                    onChange={(e) => handleChange('proyecto_fecha_inicio', e.target.value)}
                                />
                            </FormGroupHalf>
                            <FormGroupHalf>
                                <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.time_material}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                time_material: e.target.checked,
                                                proyecto_fecha_fin: e.target.checked ? '' : prev.proyecto_fecha_fin,
                                            }))
                                        }}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                    Time &amp; Material (sin fecha fin)
                                </Label>
                            </FormGroupHalf>
                            {formData.time_material && ((opportunity?.oferta as any)?.tarifas || []).length > 0 && (
                                <FormGroupHalf data-testid="tm-tarifas">
                                    <Label>
                                        Tarifas T&amp;M{' '}
                                        <TooltipIcon data-tooltip="Leídas de la oferta al importarla de SharePoint. Se factura por las horas imputadas en Redmine.">&#9432;</TooltipIcon>
                                    </Label>
                                    <div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                                        {((opportunity?.oferta as any)?.tarifas as { perfil: string; tarifa_hora: number }[]).map((t) => (
                                            <div key={t.perfil}>
                                                {t.perfil}: <strong>{t.tarifa_hora.toLocaleString('es-ES')} €/h</strong>
                                            </div>
                                        ))}
                                    </div>
                                </FormGroupHalf>
                            )}
                            {!formData.time_material && (
                                <FormGroupHalf>
                                    <Label>Fecha Fin Proyecto</Label>
                                    <Input
                                        type="date"
                                        value={formData.proyecto_fecha_fin}
                                        onChange={(e) => handleChange('proyecto_fecha_fin', e.target.value)}
                                    />
                                </FormGroupHalf>
                            )}
                            <FormGroupHalf>
                                <Label>Infraestructura</Label>
                                <Select
                                    value={formData.proyecto_infra}
                                    onChange={(e) => handleChange('proyecto_infra', e.target.value)}
                                >
                                    {infraOptions.length > 0 ? (
                                        infraOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="">Sin asignar</option>
                                            <option value="local">🏢 Local (oficina)</option>
                                            <option value="cliente">🤝 Infraestructura del Cliente</option>
                                        </>
                                    )}
                                </Select>
                            </FormGroupHalf>
                        </FormGrid>
                        <RedmineOpportunityPanel
                            opportunityId={opportunity?.id ? parseInt(opportunity.id as string) : (autoSaveId ?? undefined)}
                            identifier={formData.redmine_codigo_imputacion}
                            onIdentifierChange={(identifier) => handleChange('redmine_codigo_imputacion', identifier)}
                            showPicker={false}
                            refreshKey={redmineRefresh}
                            onAddToTeam={addAlignedMembers}
                            teamUserIds={proyectoTeam.map((m) => m.userId)}
                            onRouteMember={updateProyectoTeamMemberRoute}
                            teamRouting={Object.fromEntries(proyectoTeam.map((m) => [m.userId, m.redmineProjectId ?? null]))}
                        />
                    </FormSection>

                    {/* Equipo Oferta */}
                    <FormSection>
                        <SectionTitle>👥 Equipo Oferta (Pre-venta) <TooltipIcon data-tooltip="Personas dedicadas a la fase de pre-venta y elaboración de la propuesta">&#9432;</TooltipIcon>
                            {getOfertaWeeks() > 0 && (
                                <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#666', marginLeft: 12 }}>
                                    Semanas: {getOfertaWeeks()} <TooltipIcon data-tooltip="Total de semanas del cronograma. La carga media se calcula como: horas / semanas / 40h">&#9432;</TooltipIcon>
                                </span>
                            )}
                        </SectionTitle>

                        <TeamSection>
                            <AddTeamRow ref={ofertaAutocompleteRef}>
                                <AutocompleteContainer style={{ flex: 1 }}>
                                    <AutocompleteInput
                                        type="text"
                                        value={newOfertaMemberSearch}
                                        onChange={(e) => {
                                            setNewOfertaMemberSearch(e.target.value)
                                            setShowOfertaDropdown(true)
                                            setHighlightedOfertaIndex(0)
                                        }}
                                        onFocus={() => setShowOfertaDropdown(true)}
                                        onKeyDown={handleOfertaKeyDown}
                                        placeholder="Buscar persona para añadir a la oferta..."
                                    />
                                    {showOfertaDropdown && newOfertaMemberSearch && filteredOfertaUsers.length > 0 && (
                                        <AutocompleteDropdown>
                                            {filteredOfertaUsers.slice(0, 10).map((user, idx) => (
                                                <AutocompleteItem
                                                    key={user.id}
                                                    isHighlighted={idx === highlightedOfertaIndex}
                                                    onClick={() => addOfertaTeamMember(user)}
                                                >
                                                    {user.name}
                                                    {user.role && (
                                                        <span style={{ color: '#666', marginLeft: 8 }}>
                                                            ({user.role})
                                                        </span>
                                                    )}
                                                </AutocompleteItem>
                                            ))}
                                        </AutocompleteDropdown>
                                    )}
                                </AutocompleteContainer>
                            </AddTeamRow>

                            {ofertaTeam.length > 0 && (
                                <TeamList>
                                    {ofertaTeam.map((member) => (
                                        <TeamMember key={member.userId} style={{ flexWrap: 'wrap' }}>
                                            <TeamMemberName>
                                                {member.userName}
                                                {member.totalHoras && <span style={{ fontSize: '0.8rem', color: '#666' }}> ({member.totalHoras}h)</span>}
                                            </TeamMemberName>
                                            <InputWithSuffix>
                                                <LoadInput
                                                    type="number"
                                                    min="0"
                                                    value={member.horasPerfil || ''}
                                                    onChange={(e) => updateOfertaTeamMemberHours(member.userId, parseInt(e.target.value) || undefined)}
                                                    placeholder="Horas"
                                                    title="Horas asignadas a esta persona (entrada manual)"
                                                    style={{
                                                        width: '62px',
                                                        background: 'white',
                                                        fontWeight: 500,
                                                    }}
                                                />
                                                <InputSuffix>h</InputSuffix>
                                            </InputWithSuffix>
                                            <TeamMemberLoad>
                                                <InputWithSuffix>
                                                    <LoadInput
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        value={member.load}
                                                        onChange={(e) => updateOfertaTeamMemberField(member.userId, 'load', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                                        title="Carga % (editable)"
                                                        style={{
                                                            width: '62px',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                    <InputSuffix>%</InputSuffix>
                                                </InputWithSuffix>
                                                {/* Slider para ajustar carga por miembro (oferta) */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
                                                    <div style={{ width: 120 }}>
                                                        <input
                                                            type="range"
                                                            min={0}
                                                            max={100}
                                                            value={member.load}
                                                            onChange={(e) => updateOfertaTeamMemberField(member.userId, 'load', parseInt(e.target.value) || 0)}
                                                            aria-label={`Carga % ${member.userName}`}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </div>
                                                    <div style={{ minWidth: 36, textAlign: 'right', fontWeight: 600 }}>{member.load}%</div>
                                                </div>
                                            </TeamMemberLoad>
                                            <TeamMemberDates
                                                name={member.userName}
                                                start={member.fechaInicio}
                                                end={member.fechaFin}
                                                onChange={(start, end) => updateTeamMemberDates(setOfertaTeam, member.userId, start, end)}
                                            />
                                            <RemoveButton
                                                onClick={() => removeOfertaTeamMember(member.userId)}
                                                title="Eliminar"
                                            >
                                                ×
                                            </RemoveButton>
                                        </TeamMember>
                                    ))}
                                </TeamList>
                            )}

                            {ofertaTeam.length === 0 && (
                                <p style={{ color: '#999', fontSize: '0.9rem', marginTop: 8 }}>
                                    No hay personas asignadas a la oferta.
                                </p>
                            )}
                        </TeamSection>
                    </FormSection>

                    {/* Equipo Proyecto */}
                    <FormSection>
                        <SectionTitle>👷 Equipo Proyecto (Ejecución) <TooltipIcon data-tooltip="Personas asignadas a la ejecución del proyecto una vez ganado">&#9432;</TooltipIcon>
                            {getProyectoWeeks() > 0 && (
                                <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#666', marginLeft: 12 }}>
                                    Semanas: {getProyectoWeeks()} <TooltipIcon data-tooltip="Total de semanas del cronograma. La carga media se calcula como: horas / semanas / 40h">&#9432;</TooltipIcon>
                                </span>
                            )}
                        </SectionTitle>

                        {formData.proyecto_estado === 'pendiente' && (
                            <div style={{
                                background: '#fff3e0',
                                border: '1px solid #ffcc80',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '12px',
                                color: '#e65100',
                                fontSize: '0.9rem',
                            }}>
                                Proyecto pendiente - al asignar personas se les notificara con las fechas estimadas
                                {formData.proyecto_fecha_inicio && ` (inicio: ${formData.proyecto_fecha_inicio}`}
                                {formData.proyecto_fecha_fin && `, fin: ${formData.proyecto_fecha_fin})`}
                                {formData.proyecto_fecha_inicio && !formData.proyecto_fecha_fin && ')'}
                            </div>
                        )}

                        {/* Unified team recommendations for all roles */}
                        <UnifiedTeamRecommendations
                            opportunityId={opportunity?.id ? parseInt(opportunity.id as string) : undefined}
                            proyectoEstado={formData.proyecto_estado}
                            roleRequirements={roleRequirements}
                            assignedMembers={proyectoTeam}
                            onAssign={(userId, displayName, rol, cargabilidad) => {
                                const newMember: TeamAssignment = {
                                    userId: userId.toString(),
                                    userName: displayName,
                                    load: cargabilidad,
                                    role: rol
                                }
                                if (!proyectoTeam.some(m => m.userId === userId.toString())) {
                                    setProyectoTeam([...proyectoTeam, newMember])
                                }
                            }}
                            onRemove={(userId) => removeProyectoTeamMember(userId)}
                            onUpdateLoad={(userId, load) => updateProyectoTeamMemberLoad(userId, load)}
                            onUpdateHours={(userId, hours) => updateProyectoTeamMemberHours(userId, hours)}
                            onOpenFullModal={(role) => {
                                setRecommendationModalRole(role)
                                setShowFullRecommendationModal(true)
                            }}
                        />

                        {/* The whole project team, with each person's own dates. Always
                            shown: with role requirements the cards above list only the
                            members of those roles, and there was no way to add someone
                            by hand or to set their dates. */}
                        <TeamSection style={{ marginTop: '16px' }}>
                            {roleRequirements.length > 0 && (
                                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', marginBottom: 6 }}>
                                    Equipo asignado: fechas, subproyecto, horas y carga de cada persona
                                </div>
                            )}
                            <AddTeamRow ref={proyectoAutocompleteRef}>
                                <AutocompleteContainer style={{ flex: 1 }}>
                                    <AutocompleteInput
                                        type="text"
                                        value={newProyectoMemberSearch}
                                        onChange={(e) => {
                                            setNewProyectoMemberSearch(e.target.value)
                                            setShowProyectoDropdown(true)
                                            setHighlightedProyectoIndex(0)
                                        }}
                                        onFocus={() => setShowProyectoDropdown(true)}
                                        onKeyDown={handleProyectoKeyDown}
                                        placeholder="Buscar persona para añadir al proyecto..."
                                    />
                                    {showProyectoDropdown && newProyectoMemberSearch && filteredProyectoUsers.length > 0 && (
                                        <AutocompleteDropdown>
                                            {filteredProyectoUsers.slice(0, 10).map((user, idx) => (
                                                <AutocompleteItem
                                                    key={user.id}
                                                    isHighlighted={idx === highlightedProyectoIndex}
                                                    onClick={() => addProyectoTeamMember(user)}
                                                >
                                                    {user.name}
                                                    {user.role && (
                                                        <span style={{ color: '#666', marginLeft: 8 }}>
                                                            ({user.role})
                                                        </span>
                                                    )}
                                                </AutocompleteItem>
                                            ))}
                                        </AutocompleteDropdown>
                                    )}
                                </AutocompleteContainer>
                            </AddTeamRow>

                            {proyectoTeam.length > 0 && (
                                <TeamList>
                                    {proyectoTeam.map((member) => (
                                        <TeamMember key={member.userId} style={{ flexWrap: 'wrap' }}>
                                            <TeamMemberName>
                                                {member.userName}
                                                {member.role && member.role !== 'member' && (
                                                    <span style={{ fontSize: '0.75rem', color: '#666' }}> · {member.role}</span>
                                                )}
                                            </TeamMemberName>
                                            {redmineSubprojects.length > 0 && (
                                                <select
                                                    value={member.redmineProjectId ?? ''}
                                                    onChange={(e) => updateProyectoTeamMemberSubproject(member.userId, e.target.value ? Number(e.target.value) : null)}
                                                    title={member.redmineProjectName ? `Imputa en ${member.redmineProjectName}` : 'Subproyecto Redmine donde imputa este rol (por perfil)'}
                                                    style={{ maxWidth: 180, fontSize: '0.75rem', padding: '2px 4px', border: '1px solid #ddd', borderRadius: 4, background: member.redmineProjectId ? '#E3F2FD' : 'white' }}
                                                >
                                                    <option value="">Subproyecto: proyecto global</option>
                                                    {redmineSubprojects.map((sp) => (
                                                        <option key={sp.id} value={sp.id}>{subprojectLabel(sp.name)}</option>
                                                    ))}
                                                </select>
                                            )}
                                            <InputWithSuffix>
                                                <LoadInput
                                                    type="number"
                                                    min="0"
                                                    value={member.horasPerfil || ''}
                                                    onChange={(e) => updateProyectoTeamMemberHours(member.userId, parseInt(e.target.value) || undefined)}
                                                    placeholder="Horas"
                                                    title="Horas asignadas a esta persona (entrada manual)"
                                                    style={{
                                                        width: '56px',
                                                        background: 'white',
                                                        fontWeight: 500,
                                                    }}
                                                />
                                                <InputSuffix>h</InputSuffix>
                                            </InputWithSuffix>
                                            <TeamMemberLoad>
                                                <InputWithSuffix>
                                                    <LoadInput
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        value={member.load}
                                                        onChange={(e) => updateProyectoTeamMemberLoad(member.userId, Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                                        title="Carga % (editable)"
                                                        style={{
                                                            width: '56px',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                    <InputSuffix>%</InputSuffix>
                                                </InputWithSuffix>
                                                {/* Slider para ajustar carga por miembro (proyecto) */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
                                                    <div style={{ width: 120 }}>
                                                        <input
                                                            type="range"
                                                            min={0}
                                                            max={100}
                                                            value={member.load}
                                                            onChange={(e) => updateProyectoTeamMemberLoad(member.userId, parseInt(e.target.value) || 0)}
                                                            aria-label={`Carga % ${member.userName}`}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </div>
                                                    <div style={{ minWidth: 36, textAlign: 'right', fontWeight: 600 }}>{member.load}%</div>
                                                </div>
                                            </TeamMemberLoad>
                                            <TeamMemberDates
                                                name={member.userName}
                                                start={member.fechaInicio}
                                                end={member.fechaFin}
                                                onChange={(start, end) => updateTeamMemberDates(setProyectoTeam, member.userId, start, end)}
                                            />
                                            <RemoveButton
                                                onClick={() => removeProyectoTeamMember(member.userId)}
                                                title="Eliminar"
                                            >
                                                ×
                                            </RemoveButton>
                                        </TeamMember>
                                    ))}
                                </TeamList>
                            )}

                            {proyectoTeam.length === 0 && (
                                <p style={{ color: '#999', fontSize: '0.9rem', marginTop: 8 }}>
                                    No hay personas asignadas al proyecto.
                                </p>
                            )}
                        </TeamSection>
                    </FormSection>

                    {/* Change Log Section - only for existing opportunities */}
                    {isEditing && (
                        <FormSection>
                            <SectionTitle>📋 Historial de Cambios</SectionTitle>
                            {changeLogLoading ? (
                                <p style={{ color: '#999', fontSize: '0.9rem' }}>Cargando historial...</p>
                            ) : changeLog.length === 0 ? (
                                <p style={{ color: '#999', fontSize: '0.9rem' }}>No hay cambios registrados.</p>
                            ) : (
                                <div style={{
                                    maxHeight: 250,
                                    overflowY: 'auto',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    fontSize: '0.85rem',
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Fecha</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Usuario</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Campo</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Anterior</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Nuevo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {changeLog.map(entry => (
                                                <tr key={entry.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: '#666' }}>
                                                        {new Date(entry.changed_at).toLocaleDateString('es-ES', {
                                                            day: '2-digit', month: '2-digit', year: 'numeric',
                                                            hour: '2-digit', minute: '2-digit',
                                                        })}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', fontWeight: 500 }}>{entry.changed_by}</td>
                                                    <td style={{ padding: '6px 10px' }}>{entry.field_changed}</td>
                                                    <td style={{ padding: '6px 10px', color: '#c0392b', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        title={entry.old_value || '(vacío)'}
                                                    >
                                                        {entry.old_value || <em style={{ color: '#999' }}>(vacío)</em>}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', color: '#27ae60', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        title={entry.new_value || '(vacío)'}
                                                    >
                                                        {entry.new_value || <em style={{ color: '#999' }}>(vacío)</em>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </FormSection>
                    )}
                </ModalBody>

                <ModalFooter>
                    <div>
                        {recordId && onDelete && (
                            <Button variant="danger" onClick={handleDelete} disabled={saving}>
                                🗑️ Eliminar
                            </Button>
                        )}
                    </div>
                    <FooterButtons>
                        <Button variant="secondary" onClick={handleClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                    </FooterButtons>
                </ModalFooter>
            </Modal>

            {/* Full Recommendation Modal */}
            {showFullRecommendationModal && opportunity?.id && (
                <RecommendationModal
                    isOpen={showFullRecommendationModal}
                    onClose={() => setShowFullRecommendationModal(false)}
                    opportunityId={parseInt(opportunity.id as string)}
                    opportunityName={formData.name}
                    roles={roleRequirements.map(r => r.role)}
                    roleRequirements={roleRequirements}
                    initialRole={recommendationModalRole || ''}
                    initialCargabilidad={
                        roleRequirements.find(r => r.role === recommendationModalRole)?.cargabilidad ?? 100
                    }
                    onAssign={(userId, displayName, rol, cargabilidad) => {
                        const newMember: TeamAssignment = {
                            userId: userId.toString(),
                            userName: displayName,
                            load: cargabilidad,
                            role: rol
                        }
                        if (!proyectoTeam.some(m => m.userId === userId.toString())) {
                            setProyectoTeam([...proyectoTeam, newMember])
                        }
                    }}
                />
            )}
        </Overlay>
    )
}
